import type { CSSProperties, ReactNode } from "react";

/**
 * Stepper horizontal (Design.md §6 / §9): nós clicáveis do wizard Criar envios.
 * Concluído (índice < atual) = ✓ azul; atual = laranja; futuro = neutro (§6).
 */
export interface StepperProps {
  steps: readonly string[];
  current: number;
  onStepClick?: (index: number) => void;
  style?: CSSProperties;
}

type StepState = "done" | "current" | "upcoming";

function stepState(index: number, current: number): StepState {
  if (index < current) {
    return "done";
  }
  if (index === current) {
    return "current";
  }
  return "upcoming";
}

function nodeBackground(state: StepState): string {
  if (state === "done") {
    return "var(--blue)";
  }
  if (state === "current") {
    return "var(--orange)";
  }
  return "var(--panel-alt)";
}

function nodeBorder(state: StepState): string {
  if (state === "done") {
    return "var(--blue)";
  }
  if (state === "current") {
    return "var(--orange)";
  }
  return "var(--border)";
}

export function Stepper({
  steps,
  current,
  onStepClick,
  style
}: StepperProps): ReactNode {
  const lastIndex = steps.length - 1;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
        ...(style ?? {})
      }}
    >
      {steps.map((label, index) => {
        const state = stepState(index, current);
        const isLast = index === lastIndex;
        const clickable = onStepClick !== undefined;
        return (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              flex: isLast ? "0 0 auto" : "1 1 0",
              minWidth: 0
            }}
          >
            <button
              type="button"
              onClick={clickable ? () => onStepClick(index) : undefined}
              disabled={!clickable}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.45rem",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: clickable ? "pointer" : "default",
                color: state === "upcoming" ? "var(--text-muted)" : "var(--text)",
                minWidth: 0
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 24,
                  height: 24,
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  background: nodeBackground(state),
                  border: `2px solid ${nodeBorder(state)}`,
                  color:
                    state === "upcoming" ? "var(--text-muted)" : "var(--on-orange)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                {state === "done" ? "✓" : index + 1}
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: state === "current" ? 700 : 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {label}
              </span>
            </button>
            {!isLast ? (
              <span
                aria-hidden
                style={{
                  flex: "1 1 auto",
                  height: 2,
                  margin: "0 0.5rem",
                  background:
                    state === "done" ? "var(--blue)" : "var(--border)"
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
